<template>
  <div class="biens-container">
    <div class="biens-header">
      <h2>Gestion des Biens</h2>
      <p>Gérez votre portefeuille immobilier et vos propriétés</p>
    </div>

    <div class="biens-content">
      <!-- Search and Filters -->
      <div class="biens-toolbar">
        <div class="search-section">
          <div class="search-bar">
            <input 
              v-model="searchQuery"
              type="text"
              placeholder="Rechercher par adresse, référence..."
              class="search-input"
              @input="debouncedSearch"
            />
            <i class="search-icon">🔍</i>
          </div>
        </div>

        <div class="filters-section">
          <select v-model="typeFilter" class="filter-select" @change="applyFilters">
            <option value="">Tous les types</option>
            <option value="apartment">Appartement</option>
            <option value="shop">Magasin</option>
            <option value="office">Bureau</option>
            <option value="house">Maison</option>
            <option value="land">Terrain</option>
          </select>
          <select v-model="statutFilter" class="filter-select" @change="applyFilters">
            <option value="">Tous statuts</option>
            <option value="disponible">Disponible</option>
            <option value="occupe">Occupé</option>
            <option value="maintenance">En maintenance</option>
          </select>
          <button @click="showCreateModal = true" class="btn-primary">
            + Nouveau Bien
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading">
        <p>Chargement des biens...</p>
      </div>

      <!-- Biens Grid -->
      <div v-else-if="biens.length > 0" class="biens-grid">
        <div 
          v-for="bien in biens.filter(b => b && b.id)" 
          :key="bien.id"
          class="bien-card"
        >
          <div class="bien-header">
            <h3>{{ bien.adresse || 'Adresse non spécifiée' }}</h3>
            <div class="bien-badges">
              <span class="type-badge" :class="bien.type">
                {{ getTypeLabel(bien.type) }}
              </span>
              <span class="statut-badge" :class="bien.statut">
                {{ getStatutLabel(bien.statut) }}
              </span>
            </div>
          </div>

          <div class="bien-info">
            <div class="info-row" v-if="bien.reference">
              <strong>Référence:</strong>
              <span>{{ bien.reference }}</span>
            </div>
            <div class="info-row" v-if="bien.surface">
              <strong>Surface:</strong>
              <span>{{ bien.surface }} m²</span>
            </div>
            <div class="info-row">
              <strong>Prix:</strong>
              <span class="price">{{ formatPrice(bien.prix_location) }} DH/mois</span>
            </div>
          </div>

          <div class="bien-actions">
            <button @click="editBien(bien)" class="btn-edit">
              Modifier
            </button>
            <button @click="viewBienDetails(bien)" class="btn-info">
              Détails
            </button>
            <button @click="deleteBien(bien)" class="btn-delete">
              Supprimer
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <div class="empty-icon">🏠</div>
        <h3>Aucun bien trouvé</h3>
        <p>Commencez par ajouter votre premier bien immobilier</p>
        <button @click="showCreateModal = true" class="btn-primary">
          Ajouter un bien
        </button>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showCreateModal || showEditModal" class="modal-overlay" @click.self="closeModals">
      <div class="bien-modal">
        <div class="modal-header">
          <h3>{{ showEditModal ? 'Modifier le bien' : 'Nouveau bien' }}</h3>
          <button @click="closeModals" class="close-btn">&times;</button>
        </div>

        <form @submit.prevent="saveBien" class="bien-form">
          <div class="form-row">
            <div class="form-group">
              <label>Type de bien *</label>
              <select v-model="bienForm.type" required>
                <option value="">Sélectionner un type</option>
                <option value="apartment">Appartement</option>
                <option value="shop">Magasin</option>
                <option value="office">Bureau</option>
                <option value="house">Maison</option>
                <option value="land">Terrain</option>
              </select>
            </div>
            <div class="form-group">
              <label>Référence</label>
              <input type="text" v-model="bienForm.reference" placeholder="Référence unique" />
            </div>
          </div>

          <div class="form-group">
            <label>Adresse *</label>
            <input type="text" v-model="bienForm.adresse" required placeholder="Adresse complète du bien" />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Surface (m²)</label>
              <input type="number" v-model="bienForm.surface" min="1" placeholder="Surface en m²" />
            </div>
            <div class="form-group">
              <label>Prix de location (DH/mois) *</label>
              <input type="number" v-model="bienForm.prix_location" required min="0" placeholder="Prix mensuel" />
            </div>
          </div>

          <div class="form-actions">
            <button type="button" @click="closeModals" class="btn-secondary">
              Annuler
            </button>
            <button type="submit" class="btn-primary" :disabled="saving">
              {{ saving ? 'Sauvegarde...' : (showEditModal ? 'Modifier' : 'Créer') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click.self="showDeleteModal = false">
      <div class="delete-modal">
        <h3>Supprimer le bien</h3>
        <p>Êtes-vous sûr de vouloir supprimer <strong>{{ bienToDelete?.adresse }}</strong> ?</p>
        <p class="warning">Cette action est irréversible.</p>
        <div class="form-actions">
          <button @click="showDeleteModal = false" class="btn-secondary">
            Annuler
          </button>
          <button @click="confirmDelete" class="btn-danger" :disabled="deleting">
            {{ deleting ? 'Suppression...' : 'Supprimer' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import bienService from '@/services/bienService'

export default {
  name: 'BiensView',
  data() {
    return {
      biens: [],
      loading: false,
      saving: false,
      deleting: false,
      searchQuery: '',
      typeFilter: '',
      statutFilter: '',
      searchTimeout: null,
      pagination: {
        current_page: 1,
        per_page: 12,
        total: 0
      },
      
      // Modals
      showCreateModal: false,
      showEditModal: false,
      showDeleteModal: false,
      showDetailsModal: false,
      
      // Forms and selections
      bienForm: this.getEmptyBienForm(),
      bienToDelete: null,
      selectedBien: null,
      editingBienId: null
    }
  },
  mounted() {
    this.loadBiens()
  },
  methods: {
    getEmptyBienForm() {
      return {
        type: '',
        reference: '',
        adresse: '',
        surface: '',
        prix_location: '',
        nbr_chambres: '',
        nbr_salles_bain: '',
        etage: '',
        statut: 'disponible',
        description: '',
        meuble: false,
        climatise: false,
        parking: false,
        balcon: false,
        jardin: false,
        ascenseur: false
      }
    },

    async loadBiens(page = 1) {
      this.loading = true
      try {
        const filters = {}
        if (this.searchQuery) filters.search = this.searchQuery
        if (this.typeFilter) filters.type = this.typeFilter
        if (this.statutFilter) filters.statut = this.statutFilter

        const response = await bienService.getBiens(page, this.pagination.per_page, filters)
        // Filter out any null or invalid bien objects
        this.biens = (response.data || []).filter(bien => bien && bien.id)
        this.pagination = response.pagination || { current_page: 1, per_page: 12, total: 0 }
      } catch (error) {
        console.error('Error loading biens:', error)
        alert('Erreur lors du chargement des biens')
      } finally {
        this.loading = false
      }
    },

    debouncedSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.applyFilters()
      }, 300)
    },

    applyFilters() {
      this.loadBiens(1)
    },

    loadPage(page) {
      this.loadBiens(page)
    },

    editBien(bien) {
      this.editingBienId = bien.id
      this.bienForm = { ...bien }
      this.showEditModal = true
    },

    deleteBien(bien) {
      this.bienToDelete = bien
      this.showDeleteModal = true
    },

    viewBienDetails(bien) {
      this.selectedBien = bien
      this.showDetailsModal = true
    },

    async confirmDelete() {
      if (!this.bienToDelete) return
      
      this.deleting = true
      try {
        await bienService.deleteBien(this.bienToDelete.id)
        this.biens = this.biens.filter(b => b.id !== this.bienToDelete.id)
        this.showDeleteModal = false
        this.bienToDelete = null
      } catch (error) {
        console.error('Error deleting bien:', error)
        alert('Erreur lors de la suppression du bien')
      } finally {
        this.deleting = false
      }
    },

    async saveBien() {
      this.saving = true
      try {
        const bienData = { ...this.bienForm }
        
        if (this.showEditModal && this.editingBienId) {
          // Update existing bien
          const response = await bienService.updateBien(this.editingBienId, bienData)
          if (response.success) {
            const index = this.biens.findIndex(b => b.id === this.editingBienId)
            if (index !== -1) {
              this.biens[index] = response.data
            }
          }
        } else {
          // Create new bien
          const response = await bienService.createBien(bienData)
          if (response.success) {
            this.biens.unshift(response.data)
          }
        }
        
        this.closeModals()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde du bien:', error)
        alert('Erreur lors de la sauvegarde du bien')
      } finally {
        this.saving = false
      }
    },

    getTypeLabel(type) {
      const types = {
        'apartment': 'Appartement',
        'shop': 'Magasin',
        'office': 'Bureau',
        'house': 'Maison',
        'land': 'Terrain'
      }
      return types[type] || type
    },

    getStatutLabel(statut) {
      const statuts = {
        'disponible': 'Disponible',
        'occupe': 'Occupé',
        'maintenance': 'En maintenance'
      }
      return statuts[statut] || statut
    },

    formatPrice(price) {
      if (!price) return '0'
      return new Intl.NumberFormat('fr-FR').format(price)
    },

    hasFeatures(bien) {
      return bien.meuble || bien.climatise || bien.parking || bien.balcon || bien.jardin || bien.ascenseur
    },

    closeModals() {
      this.showCreateModal = false
      this.showEditModal = false
      this.showDeleteModal = false
      this.showDetailsModal = false
      this.bienForm = this.getEmptyBienForm()
      this.editingBienId = null
      this.bienToDelete = null
      this.selectedBien = null
    }
  }
}
</script> 

<style scoped>
.biens-container {
  padding: 32px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

.biens-header {
  text-align: center;
  margin-bottom: 32px;
}

.biens-header h2 {
  margin: 0 0 8px 0;
  color: #1f2937;
  font-size: 2rem;
}

.biens-header p {
  margin: 0;
  color: #6b7280;
  font-size: 1.1rem;
}

.biens-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 24px;
}

.search-section {
  flex: 1;
  max-width: 400px;
}

.search-bar {
  position: relative;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 16px;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

.filters-section {
  display: flex;
  gap: 16px;
  align-items: center;
}

.filter-select {
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  min-width: 150px;
}

.loading {
  text-align: center;
  padding: 64px;
  color: #6b7280;
  font-size: 1.1rem;
}

.biens-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

.bien-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  word-wrap: break-word;
  box-sizing: border-box;
}

.bien-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  transform: translateY(-2px);
}

.bien-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.bien-header h3 {
  margin: 0;
  color: #1f2937;
  font-size: 1.1rem;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  margin-right: 12px;
}

.bien-badges {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.type-badge, .statut-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  text-align: center;
  flex-shrink: 0;
  white-space: nowrap;
}

.type-badge.apartment {
  background: #dbeafe;
  color: #1e40af;
}

.type-badge.house {
  background: #f0fdf4;
  color: #166534;
}

.type-badge.shop {
  background: #fef3c7;
  color: #92400e;
}

.type-badge.office {
  background: #f3e8ff;
  color: #7c3aed;
}

.type-badge.land {
  background: #fef2f2;
  color: #991b1b;
}

.statut-badge.disponible {
  background: #dcfce7;
  color: #166534;
}

.statut-badge.occupe {
  background: #fef3c7;
  color: #92400e;
}

.statut-badge.maintenance {
  background: #fee2e2;
  color: #991b1b;
}

.bien-info {
  margin-bottom: 16px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 0.875rem;
}

.info-row strong {
  color: #374151;
  min-width: 80px;
}

.info-row span {
  color: #6b7280;
  text-align: right;
  flex: 1;
  overflow: hidden;
  word-wrap: break-word;
  word-break: break-word;
  min-width: 0;
}

.price {
  color: #059669 !important;
  font-weight: 600;
}

.bien-actions {
  display: flex;
  gap: 8px;
}

.btn-edit, .btn-info, .btn-delete {
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-edit {
  background: #f3f4f6;
  color: #374151;
}

.btn-edit:hover {
  background: #e5e7eb;
}

.btn-info {
  background: #dbeafe;
  color: #1e40af;
}

.btn-info:hover {
  background: #bfdbfe;
}

.btn-delete {
  background: #fee2e2;
  color: #dc2626;
}

.btn-delete:hover {
  background: #fecaca;
}

.empty-state {
  text-align: center;
  padding: 80px 40px;
  color: #6b7280;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 16px;
}

.empty-state h3 {
  margin: 0 0 8px 0;
  color: #374151;
}

.empty-state p {
  margin: 0 0 24px 0;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.bien-modal, .delete-modal {
  background: white;
  border-radius: 12px;
  padding: 32px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.delete-modal {
  max-width: 400px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.modal-header h3 {
  margin: 0;
  color: #1f2937;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
}

.bien-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-weight: 500;
  color: #374151;
}

.form-group input,
.form-group select {
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 16px;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.btn-primary, .btn-secondary, .btn-danger {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-primary:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.btn-danger {
  background: #dc2626;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #b91c1c;
}

.btn-danger:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.warning {
  color: #dc2626;
  font-size: 0.875rem;
  margin: 8px 0;
}

/* Responsive */
@media (max-width: 768px) {
  .biens-container {
    padding: 16px;
  }
  
  .biens-toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filters-section {
    justify-content: center;
  }
  
  .biens-grid {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .bien-modal {
    padding: 24px;
  }
}
</style> 